import { useEffect, useState } from "react";
import type { LoadingLevel } from "../data/game.js";

interface Props {
  url: string;
  levels: LoadingLevel[];
  /** Resolved by parent when API call finishes; loading stays on the last level until set. */
  ready: boolean;
  onDone: () => void;
}

export function LoadingGame({ url, levels, ready, onDone }: Props) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const per = reduce ? 240 : 720;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      if (i >= levels.length - 1) {
        // Stop at the last level and wait for `ready`.
        clearInterval(id);
        setActive(levels.length - 1);
      } else {
        setActive(i);
      }
    }, per);
    return () => clearInterval(id);
  }, [levels.length]);

  useEffect(() => {
    if (!ready) return;
    // Once ready, complete the last level and trigger onDone.
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(() => {
      setActive(levels.length);
      setTimeout(onDone, reduce ? 200 : 600);
    }, 350);
    return () => clearTimeout(t);
  }, [ready, levels.length, onDone]);

  const pct = Math.min(100, Math.round((active / levels.length) * 100));

  return (
    <div className="gloading screen-enter">
      <div className="wrap gloading-inner">
        <div className="gloading-top">
          <span className="pill pill--yellow">Now playing</span>
          <span className="gpixel gloading-url">{url}</span>
        </div>

        <div className="gloading-bar gcard">
          <div className="gloading-bar-fill" style={{ width: `${pct}%` }} />
          <span className="gpixel gloading-pct">{String(pct).padStart(3, "0")}%</span>
        </div>

        <ul className="gloading-levels">
          {levels.map((lv, i) => {
            const done = i < active;
            const now = i === active;
            return (
              <li key={i} className={`gloading-level ${done ? "is-done" : now ? "is-now" : "is-wait"}`}>
                <span className="gloading-badge gpixel">
                  {done ? "✓" : now ? <span className="gloading-spin" /> : lv.lvl.replace("LEVEL ", "L")}
                </span>
                <span className="gdisplay gloading-text">{lv.line}</span>
                {done && <span className="pill pill--green gloading-clear">Clear</span>}
              </li>
            );
          })}
        </ul>

        <p className="glabel gloading-foot">Don't refresh. This is the easy level.</p>
      </div>
    </div>
  );
}
